<i18n src="@/locales/models/exported_batch.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/harvested_batch.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/harvested_package.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/package.yml" lang="yaml"></i18n>
<i18n
  src="@/locales/views/organization/process/harvest_export/edit_exported_batch.yml"
  lang="yaml"
></i18n>

<template>
  <div>
    <go-back-link :text="$t('labels.go_back_link')" :name="routeName('show_exported_batch')" :params="{
      organization_id: organization_id,
      harvest_process_id: harvest_process_id,
      exported_batch_id: exported_batch_id
    }" />
    <bread-crumbs :labels="[module_name, harvest_process.name]"></bread-crumbs>

    <alert-danger :errors_data="std_err" />

    <div class="mt-8" v-if="load_general_data">
      <div class="panel">
        <div class="panel-header">
          <h3 class="panel-header-title">
            {{ $t("labels.title") }}
          </h3>
        </div>

        <div class="panel-body">
          <div class="space-y-8 divide-y">
            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.exported_batch.batch_code") }}
                </label>
                <div class="mt-1">
                  <input type="text" v-model.trim="exported_batch.batch_code"
                    :class="['input-text', isInvalid('batch_code')]" />
                </div>
              </div>

              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.package.registered_at") }} (DD/MM/YYYY)
                </label>
                <div class="mt-1">
                  <date-form v-model="registered_at" :readonly="false" />
                </div>
              </div>

              <div class="sm:col-span-3" v-if="agro_exporter_processes.length > 1">
                <label class="label-form">
                  {{ $t("model.exported_batch.agro_exporter") }}
                </label>
                <div class="mt-1">
                  <select-form :options="agro_exporter_processes" v-model="exported_batch.agro_exporter_process_id" />
                </div>
              </div>

              <div class="sm:col-span-3" v-if="packing_house_processes.length > 1">
                <label class="label-form">
                  {{ $t("model.exported_batch.packing_house") }}
                </label>
                <div class="mt-1">
                  <select-form :options="packing_house_processes" v-model="exported_batch.packing_house_process_id"
                    :is_invalid="isInvalidSelect('exported_batch.packing_house_process')
                      " />
                </div>
              </div>

              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.exported_batch.producer") }}
                </label>
                <div class="mt-1">
                  <select-form v-if="producers.length > 1" :options="producers" label="full_label"
                    v-model="exported_batch.producer_id" :is_invalid="isInvalidSelect('producer')" />
                </div>
              </div>

              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.exported_batch.crop_field") }}
                </label>
                <div class="mt-1" v-if="crop_fields.length > 1">
                  <select-form :options="crop_fields" v-model="exported_batch.crop_field_id"
                    :is_invalid="isInvalidSelect('crop_field')" />
                </div>
                <dd class="content-data" v-else-if="crop_fields.length == 1">
                  {{ crop_fields[0].name }}
                </dd>
                <info-badge class="mt-2" v-if="crop_field?.observations" :label="crop_field?.observations"></info-badge>
              </div>

              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.package.process_line") }}
                </label>
                <div class="mt-1">
                  <select-form :options="process_lines" v-model="process_line_id" />
                </div>
              </div>
            </div>

            <Packages v-if="exported_batch.agro_exporter_process_id" v-model="exported_packages"
              :organization_id="organization_id" :crop_exp_packages="crop_exp_packages" :crop_categories="crop_categories"
              :agro_exporter_processes="agro_exporter_processes" :crop_sizes="crop_sizes"
              :agro_exporter_process_id="exported_batch.agro_exporter_process_id" />

          </div>
        </div>

        <div class="panel-footer">
          <div class="panel-footer-actions">
            <white-button @click.prevent="goToShowProcess()" :label="$t('labels.cancel_button')" />
            <span class="space-actions"></span>
            <success-button v-if="!charging_submit" @click.prevent="update()" :label="$t('labels.save_button')" />
            <success-button-processing v-else :label="$t('labels.save_button')" />
          </div>
        </div>
      </div>
    </div>
    <loading-message v-else />
  </div>
</template>

<script>
import moment from "moment";
import Requester from "@/views/organization/requesters/process/harvest_export/manage_exported_batch.js";
import Packages from "@/components/views/organization/process/harvest_export/Packages.vue";

export default {
  name: "ORGProcessHarvestExportEditExportedBatch",
  components: {
    Packages
  },
  data() {
    return {
      load_general_data: false,
      charging_submit: false,
      harvest_process: {},
      original_exported_batch: {},
      exported_batch: {
        batch_code: "",
        agro_exporter_process_id: null,
        packing_house_process_id: null,
        producer_id: null,
        crop_field_id: null
      },
      registered_at: null,
      process_line_id: null,
      exported_packages: [],
      agro_exporter_processes: [],
      packing_house_processes: [],
      producers: [],
      crop_fields: [],
      crop_varieties: [],
      crop_exp_packages: [],
      process_lines: [],
      crop_categories: [],
      crop_sizes: [],
      brands: []
    };
  },
  props: {
    organization_id: { required: true },
    harvest_process_id: { required: true },
    exported_batch_id: { required: true }
  },
  created() {
    this.checkIfAuthenticated().then(() => {
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          organization_id: this.organization_id
        }
      }).then(() => {
        this.registered_at = moment().format("YYYY-MM-DD");
        this.loadData();
      });
    });
  },
  computed: {
    exported_packages_params: function () {
      return this.exported_packages
        .filter(exp_package => Number(exp_package.quantity) > 0)
        .map(exp_package => {
          return {
            ...exp_package,
            registered_at: this.registered_at,
            process_line_id: this.process_line_id,
            producer_id: this.exported_batch.producer_id
          };
        });
    },
    crop_field: function () {
      return this.crop_fields.find(
        crop_field => crop_field.id == this.exported_batch.crop_field_id
      );
    }
  },
  methods: {
    loadData: async function () {
      await this.fetchExportedBatch();
      await this.fetchHarvestProcess();
      await this.fetchAgroExporterProcesses();
      await this.fetchPackingHouseProcesses();
      await this.fetchProducers();
      await this.fetchProcessLines();
      this.load_general_data = true;
    },
    fetchExportedBatch: async function () {
      const response = await Requester.fetchExportedBatch(
        this.organization_id,
        this.exported_batch_id
      );
      this.original_exported_batch = response.data;
      this.exported_batch = {
        batch_code: response.data.batch_code,
        agro_exporter_process_id: response.data.agro_exporter_process_id,
        packing_house_process_id: response.data.packing_house_process_id,
        producer_id: response.data.producer_id,
        crop_field_id: response.data.crop_field_id
      };
      this.process_line_id = (response.data.packages[0] || {}).process_line_id;
      this.registered_at = response.data.registered_at;
      this.exported_packages = response.data.packages.map(exp_package => {
        return {
          exporter_process_crop_package_id:
            exp_package.exporter_process_crop_package_id,
          exporter_process_crop_category_id:
            exp_package.exporter_process_crop_category_id,
          exporter_process_crop_variety_id:
            exp_package.exporter_process_crop_variety_id,
          exporter_process_crop_size_id:
            exp_package.exporter_process_crop_size_id,
          exporter_process_brand_id: exp_package.exporter_process_brand_id,
          quantity: exp_package.quantity,
          from_stock: exp_package.from_stock,
          package_quality_inspects: exp_package.package_quality_inspects.map(inspect => {
            return {
              ...inspect,
              observations: inspect.quality_inspect.observations,
              _destroy: false
            };
          }),
          id: exp_package.id,
          _destroy: false
        };
      });
    },
    fetchHarvestProcess: async function () {
      const response = await Requester.fetchHarvestProcess(
        this.organization_id,
        this.harvest_process_id
      );
      this.harvest_process = response.data;
    },
    fetchAgroExporterProcesses: async function () {
      const response = await Requester.fetchAgroExporterProcesses(
        this.organization_id,
        this.harvest_process_id
      );
      this.agro_exporter_processes = response.data;
      this.exported_batch.agro_exporter_process_id = this.hasOneItem(
        this.agro_exporter_processes
      );
    },
    fetchPackingHouseProcesses: async function () {
      const response = await Requester.fetchPackingHouseProcesses(
        this.organization_id,
        this.harvest_process_id
      );
      this.packing_house_processes = response.data;
      this.exported_batch.packing_house_process_id = this.hasOneItem(
        this.packing_house_processes
      );
    },
    fetchProducers: async function () {
      const response = await Requester.fetchProducers(this.organization_id);
      this.producers = response.data;
      this.producers.forEach(producer => {
        producer.full_label = `${producer.organization_producer_code} - ${producer.fullname}`;
      });
    },
    fetchProcessLines: async function () {
      const response = await Requester.fetchProcessLines(
        this.organization_id,
        this.harvest_process_id
      );
      this.process_lines = response.data;
    },
    fetchCropFields: async function () {
      const response = await Requester.fetchCropFields(
        this.organization_id,
        this.harvest_process_id,
        this.exported_batch.producer_id
      );
      this.crop_fields = response.data;
      if (this.exported_batch.producer_id == this.original_exported_batch.producer_id) {
        this.exported_batch.crop_field_id = this.original_exported_batch.crop_field_id
      } else {
        this.exported_batch.crop_field_id = this.hasOneItem(this.crop_fields);
      }
    },
    fetchOrganizationCropField: async function () {
      const response = await Requester.fetchOrganizationCropField(
        this.organization_id,
        this.exported_batch.crop_field_id
      );
      this.process_line_id = response.data.process_line_id;
    },
    fetchCropCategories: async function () {
      const response = await Requester.fetchExporterProcessCropCategories(
        this.organization_id,
        this.exported_batch.agro_exporter_process_id
      );
      this.crop_categories = response.data;
    },
    fetchCropVarieties: async function () {
      const response = await Requester.fetchExporterProcessCropVarieties(
        this.organization_id,
        this.exported_batch.agro_exporter_process_id
      );
      this.crop_varieties = response.data;
    },
    fetchCropSizes: async function () {
      const response = await Requester.fetchExporterProcessCropSizes(
        this.organization_id,
        this.exported_batch.agro_exporter_process_id
      );
      this.crop_sizes = response.data;
    },
    fetchAgroExporterBrands: async function () {
      const response = await Requester.fetchExporterProcessBrands(
        this.organization_id,
        this.exported_batch.agro_exporter_process_id
      );
      this.brands = response.data;
    },
    fetchCropExpPackages: async function () {
      const response = await Requester.fetchExporterProcessCropPackages(
        this.organization_id,
        this.exported_batch.agro_exporter_process_id
      );
      this.crop_exp_packages = response.data;
    },
    loadPackageData: async function () {
      await this.fetchCropCategories();
      await this.fetchCropVarieties();
      await this.fetchCropSizes();
      await this.fetchAgroExporterBrands();
      await this.fetchCropExpPackages();
    },
    update: async function () {
      this.charging_submit = true;
      try {
        let response = await Requester.update(
          this.organization_id,
          this.harvest_process_id,
          this.exported_batch_id,
          {
            ...this.exported_batch,
            registered_at: this.registered_at,
            packages: this.exported_packages_params
          }
        );

        this.registerSuccessNotification({
          title: response.data.success_message
        });
        this.goToShowProcess();
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields
          });
        }
      }
      this.charging_submit = false;
    },
    goToShowProcess: function () {
      this.$router.push({
        name: this.routeName("show_exported_batch"),
        params: {
          organization_id: this.organization_id,
          harvest_process_id: this.harvest_process_id,
          exported_batch_id: this.exported_batch_id
        }
      });
    },
    hasOneItem: function (array) {
      return array.length == 1 ? array[0].id : null;
    }
  },
  watch: {
    "exported_batch.producer_id": function (new_value) {
      this.exported_batch.crop_field_id = null;
      this.exported_batch.crop_variety_id = null;
      this.crop_fields = [];
      if (new_value) this.fetchCropFields();
    },
    "exported_batch.crop_field_id": function (new_value) {
      this.process_line_id = null;
      if (new_value) {
        this.exported_batch.crop_variety_id = this.crop_field.crop_variety_id;
        this.fetchOrganizationCropField();
      }
    },
    "exported_batch.agro_exporter_process_id": function () {
      this.loadPackageData();
    }
  }
};
</script>
